import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./baseStyles.css";

const apiUrl = process.env.REACT_APP_API_URL;

const quickAddApiUrl = apiUrl?.replace("/campaign", "/quickadd");


const MyOrganizerQuickAddPage = ({ tokenMode = false }) => {
  const navigate = useNavigate();
  const { captureToken } = useParams();

  const [userid, setUserid] = useState("");
  const [rawText, setRawText] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [listening, setListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);
  const shouldSubmitAfterStopRef = useRef(false);
  const rawTextRef = useRef("");

  useEffect(() => {
    rawTextRef.current = rawText;
  }, [rawText]);

  const isTokenCapture = useMemo(
    () => Boolean(tokenMode || captureToken),
    [tokenMode, captureToken]
  );

  useEffect(() => {
    if (isTokenCapture) return;

    const idToken = localStorage.getItem("idToken");
    if (!idToken) {
      navigate("/login");
      return;
    }

    try {
      const decodedToken = jwtDecode(idToken);

      if (decodedToken.exp * 1000 < Date.now()) {
        localStorage.removeItem("idToken");
        navigate("/login");
        return;
      }

      setUserid(decodedToken.sub);
    } catch (err) {
      console.error("Error decoding token:", err);
      localStorage.removeItem("idToken");
      navigate("/login");
    }
  }, [isTokenCapture, navigate]);

  const handleSubmit = async (textOverride = null) => {
    const text = (typeof textOverride === "string" ? textOverride : rawTextRef.current).trim();


    if (isTokenCapture && !captureToken) {
      setErrorMessage("Missing quick capture token.");
      setStatusMessage("");
      return;
    }


    if (!text) {
      setErrorMessage("Enter something to capture.");
      setStatusMessage("");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      const payload = isTokenCapture
        ? {
            operation: "myorganizer_capture_with_token",
            captureToken,
            rawText: text,
            source: "token_quick_add",
          }
        : {
            operation: "myorganizer_capture_create",
            userid,
            rawText: text,
            source: "logged_in_quick_add",
          };

      const config = isTokenCapture
        ? {}
        : {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("idToken")}`,
            },
          };


      const postUrl = isTokenCapture ? quickAddApiUrl : apiUrl;
      const response = await axios.post(postUrl, payload, config);

      setStatusMessage(response.data?.message || "Got it.");
      setRawText("");
    } catch (err) {
      console.error("myOrganizer capture failed:", err);
      setErrorMessage(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Capture failed. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setSpeechSupported(false);
      return undefined;
    }

    setSpeechSupported(true);

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || "en-US";

    let startingText = "";
    let finalTranscript = "";

    recognition.onstart = () => {
      startingText = rawTextRef.current.trim();
      finalTranscript = "";
      setErrorMessage("");
      setStatusMessage("");
      setListening(true);
    };

    recognition.onresult = (event) => {
      let interimTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      const spokenText = `${finalTranscript} ${interimTranscript}`.trim();
      const combinedText = [startingText, spokenText].filter(Boolean).join(" ");
      rawTextRef.current = combinedText;
      setRawText(combinedText);
    };

    recognition.onerror = (event) => {
      shouldSubmitAfterStopRef.current = false;
      setListening(false);

      if (event.error !== "aborted" && event.error !== "no-speech") {
        setErrorMessage(
          "Voice capture is not available right now. You can still type or use your phone's keyboard microphone."
        );
      }
    };

    recognition.onend = () => {
      setListening(false);

      if (shouldSubmitAfterStopRef.current) {
        shouldSubmitAfterStopRef.current = false;
        const textToSubmit = rawTextRef.current.trim();
        if (textToSubmit) {
          handleSubmit(textToSubmit);
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      shouldSubmitAfterStopRef.current = false;
      recognition.abort();
      recognitionRef.current = null;
    };
  }, []);

  const handleVoiceButton = () => {
    const recognition = recognitionRef.current;
    if (!recognition || saving) return;

    if (listening) {
      shouldSubmitAfterStopRef.current = true;
      recognition.stop();
      return;
    }

    shouldSubmitAfterStopRef.current = false;
    setErrorMessage("");
    setStatusMessage("");

    try {
      recognition.start();
    } catch (err) {
      console.error("Unable to start voice capture:", err);
      setErrorMessage(
        "Voice capture could not start. You can still type or use your phone's keyboard microphone."
      );
    }
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div className="container" style={{ maxWidth: "560px" }}>
      <h2 className="header">myOrganizer</h2>

      <p style={{ textAlign: "center", marginTop: "-6px" }}>
        Quick capture now. Sort it later.
      </p>

      <div
        style={{
          background: "white",
          borderRadius: "14px",
          padding: "18px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          marginTop: "18px",
        }}
      >
        <label
          htmlFor="myorganizer-raw-text"
          style={{ display: "block", fontWeight: "600", marginBottom: "8px" }}
        >
          What do you want to remember?
        </label>

        <textarea
          id="myorganizer-raw-text"
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Example: need olive oil, batteries, and pesto"
          rows={4}
          style={{
            width: "100%",
            boxSizing: "border-box",
            fontSize: "1rem",
            padding: "12px",
            border: "1px solid #ccc",
            borderRadius: "10px",
            resize: "vertical",
          }}
        />

        {speechSupported && (
          <button
            className="save-button"
            type="button"
            onClick={handleVoiceButton}
            disabled={saving}
            style={{
              width: "100%",
              marginTop: "14px",
              minHeight: "56px",
              fontSize: "1.15rem",
              fontWeight: "700",
            }}
          >
            {listening ? "■ Stop & Save" : "🎤 Speak"}
          </button>
        )}

        <button
          className="save-button"
          type="button"
          onClick={() => handleSubmit()}
          disabled={saving}
          style={{ width: "100%", marginTop: speechSupported ? "8px" : "14px" }}
        >
          {saving ? "Saving..." : speechSupported ? "Save typed text" : "Save to myOrganizer"}
        </button>

        {statusMessage && (
          <p className="success-message" style={{ textAlign: "center" }}>
            {statusMessage}
          </p>
        )}

        {errorMessage && (
          <p className="error-message" style={{ textAlign: "center" }}>
            {errorMessage}
          </p>
        )}
      </div>

      {!isTokenCapture && (
        <p style={{ fontSize: "0.9rem", textAlign: "center", marginTop: "14px" }}>
          Logged-in quick add. Your permanent phone link will use /q/your-token.
        </p>
      )}
    </div>
  );
};

export default MyOrganizerQuickAddPage;
